import defaultLocationGpsConfig from './locationGpsDefaults.json';

// Ubicaciones GPS del mapa general (Toluca de Lerdo, Edomex).
// `ceu` es la Ciudad Universitaria UAEM (alias `cu`): se mantiene el id
// `ceu` para seguir en sincronía con el mapa, los puntos ocultos y el catálogo
// compartido de ambientaciones.
export const GPS_LOCATION_IDS = ['centro', 'ceu', 'tollocan', 'norte', 'parque'];

export const GPS_LOCATION_ALIASES = { cu: 'ceu' };

// Zona de juego válida (backend/playAttempts VALID_ZONES) por ubicación
// del mapa. Comparte el criterio del catálogo de ambientaciones.
export const GPS_LOCATION_TO_GAME_ZONE = {
  centro: 'Centro Histórico',
  ceu: 'Paseo Colón',
  norte: 'Toluca',
  tollocan: 'Toluca',
  parque: 'Toluca',
};

const clone = (value) => JSON.parse(JSON.stringify(value));

export const getDefaultLocationGpsConfig = () => clone(defaultLocationGpsConfig);

export const normalizeLocationId = (input) => {
  const raw = String(input || '').trim().toLowerCase();
  if (GPS_LOCATION_IDS.includes(raw)) return raw;
  if (raw && GPS_LOCATION_ALIASES[raw]) return GPS_LOCATION_ALIASES[raw];
  return null;
};

// Distancia Haversine en km entre dos coordenadas.
export const haversineKm = (latA, lonA, latB, lonB) => {
  const toRad = (degrees) => (Number(degrees) * Math.PI) / 180;
  const earthKm = 6371;
  const dLat = toRad(latB - latA);
  const dLon = toRad(lonB - lonA);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(latA)) * Math.cos(toRad(latB)) * Math.sin(dLon / 2) ** 2;
  return 2 * earthKm * Math.asin(Math.min(1, Math.sqrt(a)));
};

// Resuelve la ubicación del mapa general más cercana a (lat, lon).
// Devuelve la más cercana dentro de su radio detectable; si ninguna cubre
// el punto, `locationId` es null pero `nearest` trae la más cercana para
// poder asignar una zona aproximada sin pin.
export const resolveGpsLocation = (lat, lon, config) => {
  const latitude = Number(lat);
  const longitude = Number(lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  const locations = config?.locations;
  if (!locations || typeof locations !== 'object') return null;

  let nearest = null;
  let match = null;
  for (const id of GPS_LOCATION_IDS) {
    const entry = locations[id];
    if (!entry || !Number.isFinite(Number(entry.lat)) || !Number.isFinite(Number(entry.lon))) continue;
    const radiusKm = Number(entry.radiusKm);
    if (!Number.isFinite(radiusKm) || radiusKm <= 0) continue;
    const distanceKm = haversineKm(latitude, longitude, Number(entry.lat), Number(entry.lon));
    const candidate = {
      locationId: id,
      label: String(entry.label || id),
      distanceKm,
      radiusKm,
    };
    if (!nearest || distanceKm < nearest.distanceKm) nearest = candidate;
    if (distanceKm <= radiusKm && (!match || distanceKm < match.distanceKm)) match = candidate;
  }
  if (!nearest) return null;
  return {
    locationId: match ? match.locationId : null,
    label: match ? match.label : null,
    distanceKm: match ? match.distanceKm : nearest.distanceKm,
    radiusKm: match ? match.radiusKm : nearest.radiusKm,
    inside: Boolean(match),
    nearest,
  };
};

export const getGameZoneForGpsLocation = (locationId, fallback = 'Toluca') => (
  GPS_LOCATION_TO_GAME_ZONE[normalizeLocationId(locationId)] || fallback
);
